import { Button, ErrorState, Input, PageHeader, Segmented, Select, Skeleton } from '@pm/ui';
import { endOfMonth, format, startOfMonth, subDays } from 'date-fns';
import { BarChart3 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { PRIORITY_LABEL, STATUS_LABEL } from '@/lib/format';
import { useContexts, useDashboard, useTeams } from '@/lib/queries';
import { BarList, ColumnChart, Panel, StatCard } from './charts';

type Preset = 'month' | '30d' | '90d' | 'custom';
const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function presetRange(p: Preset): { from: string; to: string } {
  const now = new Date();
  if (p === 'month')
    return { from: startOfMonth(now).toISOString(), to: endOfMonth(now).toISOString() };
  if (p === '30d') return { from: subDays(now, 30).toISOString(), to: now.toISOString() };
  return { from: subDays(now, 90).toISOString(), to: now.toISOString() };
}

/** SPEC §42 / FRONTEND_UX §9 — cards e gráficos; tarefas pessoais nunca entram (RULE-030). */
export function DashboardPage() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<Preset>('month');
  const [custom, setCustom] = useState({
    from: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd'),
  });
  const [contextId, setContextId] = useState('');
  const [teamId, setTeamId] = useState('');
  const { data: contexts = [] } = useContexts({ status: 'ACTIVE' });
  const { data: teams = [] } = useTeams();
  const range =
    preset === 'custom'
      ? {
          from: new Date(`${custom.from}T00:00:00`).toISOString(),
          to: new Date(`${custom.to}T23:59:59`).toISOString(),
        }
      : presetRange(preset);
  const { data, isLoading, isError, error, refetch } = useDashboard({
    ...range,
    context_id: contextId || undefined,
    team_id: teamId || undefined,
  });

  const goList = (q: Record<string, string>) =>
    navigate(`/demandas?${new URLSearchParams(q).toString()}`);

  return (
    <div className="flex flex-col gap-gap-lg">
      <PageHeader
        title="Dashboard"
        subtitle="Visão corporativa da operação. Tarefas pessoais não entram."
        actions={
          <>
            <Segmented
              ariaLabel="Período"
              value={preset}
              onChange={setPreset}
              options={[
                { value: 'month', label: 'Este mês' },
                { value: '30d', label: '30 dias' },
                { value: '90d', label: '90 dias' },
                { value: 'custom', label: 'Período' },
              ]}
            />
            {preset === 'custom' ? (
              <span className="flex items-center gap-1">
                <Input
                  type="date"
                  mono
                  value={custom.from}
                  onChange={(e) => setCustom({ ...custom, from: e.target.value })}
                  className="h-control-sm w-[140px] text-sm"
                />
                <span className="text-text-3">→</span>
                <Input
                  type="date"
                  mono
                  value={custom.to}
                  onChange={(e) => setCustom({ ...custom, to: e.target.value })}
                  className="h-control-sm w-[140px] text-sm"
                />
              </span>
            ) : null}
            <Select
              value={contextId}
              onChange={(e) => setContextId(e.target.value)}
              className="h-control-sm w-[180px] text-sm"
              aria-label="Contexto"
            >
              <option value="">Todos os contextos</option>
              {contexts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Select
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="h-control-sm w-[160px] text-sm"
              aria-label="Time"
            >
              <option value="">Todos os times</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
            <Button variant="ghost" onClick={() => navigate('/relatorios')}>
              <BarChart3 /> Relatórios
            </Button>
          </>
        }
      />
      {isError ? <ErrorState message={error.message} onRetry={() => void refetch()} /> : null}
      {isLoading || !data ? (
        <div className="grid gap-gap sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-[76px] rounded-md" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-gap sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            <StatCard
              label="Pendentes"
              value={data.cards.pending}
              onClick={() =>
                goList({ status: 'NOT_STARTED,IN_PROGRESS,WAITING,BLOCKED,PENDING_APPROVAL' })
              }
            />
            <StatCard
              label="Atrasadas"
              value={data.cards.overdue}
              tone={data.cards.overdue > 0 ? 'danger' : undefined}
              onClick={() => goList({ overdue: 'true' })}
            />
            <StatCard
              label="Não iniciadas"
              value={data.cards.not_started}
              onClick={() => goList({ status: 'NOT_STARTED' })}
            />
            <StatCard
              label="Concluídas no período"
              value={data.cards.completed_in_period}
              tone="success"
              hint={
                data.cards.completed_on_time_pct !== null
                  ? `${data.cards.completed_on_time_pct}% no prazo`
                  : undefined
              }
            />
            <StatCard
              label="Aguardando aprovação"
              value={data.cards.pending_approval}
              tone={data.cards.pending_approval > 0 ? 'warning' : undefined}
              onClick={() => goList({ status: 'PENDING_APPROVAL' })}
            />
            <StatCard
              label="Não atribuídas"
              value={data.cards.unassigned}
              tone={data.cards.unassigned > 0 ? 'warning' : undefined}
            />
            <StatCard
              label="Período"
              value={
                <span className="text-sm">
                  {format(new Date(data.period.from), 'dd/MM')} →{' '}
                  {format(new Date(data.period.to), 'dd/MM')}
                </span>
              }
            />
          </div>

          <div className="grid gap-gap-lg lg:grid-cols-2">
            <Panel title="Abertas por usuário">
              <BarList
                items={data.open_by_user}
                onSelect={(u) => u.id && goList({ responsible_user_id: u.id })}
              />
            </Panel>
            <Panel title="Concluídas por usuário (período)">
              <BarList items={data.completed_by_user} color="var(--success)" />
            </Panel>
            <Panel title="Concluídas por time (período)">
              <BarList items={data.completed_by_team} color="var(--success)" />
            </Panel>
            <Panel title="Demandas por cliente (criadas no período)">
              <BarList
                items={data.tasks_by_client}
                onSelect={(c) => c.id && goList({ context_id: c.id })}
              />
            </Panel>
            <Panel title="Clientes que mais demandam (abertas agora)">
              <BarList
                items={data.top_clients_open}
                color="var(--warning)"
                onSelect={(c) => c.id && goList({ context_id: c.id })}
              />
            </Panel>
            <Panel
              title="Entregas previstas por dia da semana"
              action={
                <Button variant="ghost" size="sm" onClick={() => navigate('/semana')}>
                  Ver semana
                </Button>
              }
            >
              <ColumnChart
                items={data.deliveries_by_weekday.map((d) => ({
                  key: String(d.weekday),
                  label: WEEKDAYS[d.weekday - 1]!,
                  count: d.count,
                  muted: d.weekday > 5,
                }))}
              />
            </Panel>
            <Panel title="Por status">
              <BarList
                items={data.by_status.map((s) => ({
                  id: s.status,
                  name: STATUS_LABEL[s.status] ?? s.status,
                  count: s.count,
                }))}
                color="var(--info)"
                onSelect={(s) => s.id && goList({ status: s.id })}
              />
            </Panel>
            <Panel title="Abertas por prioridade">
              <BarList
                items={data.by_priority.map((p) => ({
                  id: p.priority,
                  name: PRIORITY_LABEL[p.priority] ?? p.priority,
                  count: p.count,
                }))}
                color="var(--violet)"
                onSelect={(p) => p.id && goList({ priority: p.id })}
              />
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
